// Template Service for managing beautiful HTML email templates
import GeminiService from './geminiService';

export interface PersonalizationData {
  // Basic recipient info
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  title: string;
  industry: string;
  location: string;
  phone?: string;
  website?: string;
  
  // Advanced personalization
  companySize?: string;
  revenue?: string;
  recentNews?: string;
  mutualConnections?: string[];
  interests?: string[];
  painPoints?: string[];
  goals?: string[];
  timeline?: string;
  budget?: string;
  decisionMakers?: string[];
  
  // Behavioral data
  emailEngagement?: 'high' | 'medium' | 'low';
  websiteActivity?: string[];
  socialActivity?: string[];
  contentPreferences?: string[];
  communicationStyle?: 'formal' | 'casual' | 'technical' | 'executive';
  
  // Custom fields
  customFields?: Record<string, any>;
  
  // Photos and attachments
  profilePhoto?: string;
  companyLogo?: string;
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
}

export interface TemplateTheme {
  name: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  primaryFont: string;
  secondaryFont?: string;
  borderRadius?: number;
  spacing?: {
    small: string;
    medium: string;
    large: string;
  };
}

export interface HTMLTemplate {
  id: string;
  name: string;
  category: 'sales' | 'followup' | 'calendar' | 'professional';
  description: string;
  htmlContent: string;
  variables: string[];
  previewImage: string;
  features: string[];
  themes: TemplateTheme[];
  defaultTheme: string;
}

export interface PersonalizedContent {
  subject: string;
  greeting: string;
  openingLine: string;
  mainContent: string;
  valueProposition: string;
  callToAction: string;
  closing: string;
  signature: string;
  personalizedInsights: string[];
  industryRelevantContent: string[];
  contextualReferences: string[];
}

export interface TemplatePersonalizationRequest {
  templateId: string;
  recipientData: PersonalizationData;
  campaignObjective: string;
  tone: 'professional' | 'friendly' | 'casual' | 'authoritative' | 'consultative';
  urgency: 'low' | 'medium' | 'high';
  includeIndustryInsights: boolean;
  includeSocialProof: boolean;
  includePersonalizedValue: boolean;
  customInstructions?: string;
}

class TemplateService {
  private static templates: HTMLTemplate[] = [
    {
      id: 'sales',
      name: 'Premium Sales Outreach',
      category: 'sales',
      description: 'Sophisticated sales template with value propositions, benefits, and social proof sections',
      htmlContent: '', // Will be loaded from backend
      variables: [
        'firstName', 'lastName', 'company', 'title', 'industry', 'location',
        'primaryColor', 'secondaryColor', 'accentColor', 'backgroundColor', 'textColor',
        'logo', 'companyName', 'greeting', 'mainContent', 'valueProposition',
        'benefits', 'socialProof', 'stats', 'ctaText', 'ctaUrl', 'senderName',
        'senderTitle', 'signature', 'trackingPixel'
      ],
      previewImage: '/templates/previews/sales.png',
      features: [
        'Gradient header design',
        'Value proposition highlight',
        'Benefits with checkmarks',
        'Social proof testimonials',
        'Statistics showcase',
        'Professional CTA buttons',
        'Mobile responsive',
        'Dark mode support'
      ],
      themes: [
        {
          name: 'Corporate Blue',
          primaryColor: '#1e40af',
          secondaryColor: '#3b82f6',
          accentColor: '#06b6d4',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        },
        {
          name: 'Success Green',
          primaryColor: '#059669',
          secondaryColor: '#10b981',
          accentColor: '#34d399',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        },
        {
          name: 'Premium Purple',
          primaryColor: '#7c3aed',
          secondaryColor: '#a855f7',
          accentColor: '#c084fc',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        }
      ],
      defaultTheme: 'Corporate Blue'
    },
    {
      id: 'followup',
      name: 'Professional Follow-up',
      category: 'followup',
      description: 'Clean follow-up template with timeline, context reminders, and next steps',
      htmlContent: '',
      variables: [
        'firstName', 'company', 'contextReminder', 'timeline', 'nextSteps',
        'valueAdd', 'availability', 'senderName', 'senderTitle', 'senderEmail'
      ],
      previewImage: '/templates/previews/followup.png',
      features: [
        'Timeline visualization',
        'Context reminder boxes',
        'Next steps checklist',
        'Professional signature',
        'Clean typography',
        'Minimal design'
      ],
      themes: [
        {
          name: 'Clean Blue',
          primaryColor: '#3b82f6',
          secondaryColor: '#1e40af',
          accentColor: '#06b6d4',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        }
      ],
      defaultTheme: 'Clean Blue'
    },
    {
      id: 'calendar',
      name: 'Calendar Booking',
      category: 'calendar',
      description: 'Meeting scheduling template with calendar integration and backup options',
      htmlContent: '',
      variables: [
        'firstName', 'meetingType', 'duration', 'calendarLink', 'alternativeTimes',
        'meetingAgenda', 'preparation', 'benefits'
      ],
      previewImage: '/templates/previews/calendar.png',
      features: [
        'Calendar integration',
        'Time slot selection',
        'Meeting agenda preview',
        'Backup scheduling options',
        'Professional meeting prep'
      ],
      themes: [
        {
          name: 'Scheduling Purple',
          primaryColor: '#7c3aed',
          secondaryColor: '#a855f7',
          accentColor: '#c084fc',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        }
      ],
      defaultTheme: 'Scheduling Purple'
    },
    {
      id: 'professional',
      name: 'Enterprise Professional',
      category: 'professional',
      description: 'Premium enterprise template with advanced styling and comprehensive sections',
      htmlContent: '',
      variables: [
        'firstName', 'lastName', 'company', 'title', 'industry', 'expertise',
        'challenges', 'solutions', 'results', 'testimonials', 'nextSteps'
      ],
      previewImage: '/templates/previews/professional.png',
      features: [
        'Enterprise-grade design',
        'Advanced CSS properties',
        'MSO Outlook support',
        'AI content blocks',
        'Multi-section layout',
        'Professional branding'
      ],
      themes: [
        {
          name: 'Executive Dark',
          primaryColor: '#1f2937',
          secondaryColor: '#374151',
          accentColor: '#3b82f6',
          backgroundColor: '#ffffff',
          textColor: '#1f2937',
          primaryFont: 'Inter'
        }
      ],
      defaultTheme: 'Executive Dark'
    }
  ];

  static async loadTemplateContent(templateId: string): Promise<string> {
    try {
      // Try to load from backend first
      const response = await fetch(`http://localhost:3001/api/templates/designs/${templateId}`);
      if (!response.ok) {
        throw new Error(`Failed to load template: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Failed to load template content from backend:', error);
      // Fallback to basic template
      return this.getBasicTemplate();
    }
  }

  static getTemplates(): HTMLTemplate[] {
    return this.templates;
  }

  static getTemplate(id: string): HTMLTemplate | undefined {
    return this.templates.find(template => template.id === id);
  }

  static getTemplatesByCategory(category: string): HTMLTemplate[] {
    return this.templates.filter(template => template.category === category);
  }

  static async personalizeTemplate(request: TemplatePersonalizationRequest): Promise<string> {
    const template = this.getTemplate(request.templateId);
    if (!template) {
      throw new Error(`Template not found: ${request.templateId}`);
    }

    // Generate AI-powered personalized content
    const personalizedContent = await this.generatePersonalizedContent(request);
    
    // Load template HTML
    const htmlContent = await this.loadTemplateContent(request.templateId);
    
    // Merge personalized content with template
    return this.renderTemplate(htmlContent, personalizedContent, request.recipientData);
  }

  private static async generatePersonalizedContent(
    request: TemplatePersonalizationRequest
  ): Promise<PersonalizedContent> {
    const { recipientData, campaignObjective, tone, urgency } = request;

    const prompt = `
Create highly personalized email content for this recipient:

RECIPIENT INFO:
- Name: ${recipientData.firstName} ${recipientData.lastName}
- Title: ${recipientData.title}
- Company: ${recipientData.company}
- Industry: ${recipientData.industry}
- Location: ${recipientData.location}
- Company Size: ${recipientData.companySize || 'Unknown'}
- Recent News: ${recipientData.recentNews || 'None'}
- Pain Points: ${recipientData.painPoints?.join(', ') || 'General business challenges'}
- Goals: ${recipientData.goals?.join(', ') || 'Business growth'}
- Communication Style: ${recipientData.communicationStyle || 'professional'}

CAMPAIGN DETAILS:
- Objective: ${campaignObjective}
- Tone: ${tone}
- Urgency: ${urgency}
- Include Industry Insights: ${request.includeIndustryInsights}
- Include Social Proof: ${request.includeSocialProof}

REQUIREMENTS:
Create email content that feels genuinely human and personally crafted. Use specific details about their company, industry, and situation. Make references that show you've done research.

Return JSON with this structure:
{
  "subject": "Personalized subject line",
  "greeting": "Personalized greeting based on time/context",
  "openingLine": "Compelling opening that references specific context",
  "mainContent": "Main email body with personalized value",
  "valueProposition": "Specific value prop for their situation",
  "callToAction": "Context-appropriate CTA",
  "closing": "Professional closing",
  "signature": "Sender signature block",
  "personalizedInsights": ["insight 1", "insight 2", "insight 3"],
  "industryRelevantContent": ["relevant point 1", "relevant point 2"],
  "contextualReferences": ["reference 1", "reference 2"]
}

Make it feel like you personally know them and their business challenges.
`;

    try {
      const response = await GeminiService.enhanceEmailContent(
        `Personalized outreach for ${recipientData.firstName}`,
        prompt,
        `${recipientData.title} in ${recipientData.industry}`,
        campaignObjective
      );

      // Parse the AI response and extract structured content
      const aiContent = this.parseAIResponse(response.content);
      
      return {
        subject: aiContent.subject || `${recipientData.firstName}, quick question about ${recipientData.company}`,
        greeting: aiContent.greeting || `Hi ${recipientData.firstName},`,
        openingLine: aiContent.openingLine || `I hope this email finds you well.`,
        mainContent: aiContent.mainContent || '',
        valueProposition: aiContent.valueProposition || '',
        callToAction: aiContent.callToAction || 'Would you be open to a brief conversation?',
        closing: aiContent.closing || 'Best regards,',
        signature: aiContent.signature || '',
        personalizedInsights: aiContent.personalizedInsights || [],
        industryRelevantContent: aiContent.industryRelevantContent || [],
        contextualReferences: aiContent.contextualReferences || []
      };
    } catch (error) {
      console.error('AI personalization failed:', error);
      return this.getFallbackContent(recipientData);
    }
  }

  private static parseAIResponse(content: string): Partial<PersonalizedContent> {
    try {
      // Try to extract JSON from the AI response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
      return {};
    } catch (error) {
      console.error('Failed to parse AI response:', error);
      return {};
    }
  }

  private static getFallbackContent(recipientData: PersonalizationData): PersonalizedContent {
    return {
      subject: `${recipientData.firstName}, quick question about ${recipientData.company}`,
      greeting: `Hi ${recipientData.firstName},`,
      openingLine: `I hope this email finds you well.`,
      mainContent: `I wanted to reach out because I believe there's an opportunity to help ${recipientData.company} achieve its goals.`,
      valueProposition: `Based on what I know about ${recipientData.industry} companies, there are significant opportunities for growth and efficiency improvements.`,
      callToAction: 'Would you be open to a brief 15-minute conversation this week?',
      closing: 'Best regards,',
      signature: '',
      personalizedInsights: [],
      industryRelevantContent: [],
      contextualReferences: []
    };
  }

  private static renderTemplate(
    htmlContent: string, 
    personalizedContent: PersonalizedContent, 
    recipientData: PersonalizationData
  ): string {
    let rendered = htmlContent;

    // Replace all variables with personalized content
    const replacements = {
      // Basic personalization
      '{{firstName}}': recipientData.firstName,
      '{{lastName}}': recipientData.lastName,
      '{{company}}': recipientData.company,
      '{{title}}': recipientData.title,
      '{{industry}}': recipientData.industry,
      '{{location}}': recipientData.location,
      
      // AI-generated content
      '{{subject}}': personalizedContent.subject,
      '{{greeting}}': personalizedContent.greeting,
      '{{mainContent}}': personalizedContent.mainContent,
      '{{valueProposition}}': personalizedContent.valueProposition,
      '{{callToAction}}': personalizedContent.callToAction,
      
      // Template styling (use defaults if not specified)
      '{{primaryColor}}': '#1e40af',
      '{{secondaryColor}}': '#3b82f6',
      '{{accentColor}}': '#06b6d4',
      '{{backgroundColor}}': '#ffffff',
      '{{textColor}}': '#1f2937',
      '{{primaryFont}}': 'Inter, Arial, sans-serif'
    };

    // Apply all replacements
    Object.entries(replacements).forEach(([variable, value]) => {
      const regex = new RegExp(variable.replace(/[{}]/g, '\\$&'), 'g');
      rendered = rendered.replace(regex, value || '');
    });

    return rendered;
  }

  private static getBasicTemplate(): string {
    return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>{{subject}}</title>
    <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
        .container { max-width: 600px; margin: 0 auto; background: #fff; padding: 30px; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
        .header { text-align: center; margin-bottom: 30px; }
        .content { margin-bottom: 30px; }
        .cta { text-align: center; margin: 30px 0; }
        .cta a { background: #007bff; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>{{greeting}}</h1>
        </div>
        <div class="content">
            <p>{{mainContent}}</p>
        </div>
        <div class="cta">
            <a href="#" target="_blank">{{callToAction}}</a>
        </div>
    </div>
</body>
</html>`;
  }

  static async generateTemplatePreview(
    templateId: string, 
    sampleData?: Partial<PersonalizationData>
  ): Promise<string> {
    const defaultSampleData: PersonalizationData = {
      firstName: 'John',
      lastName: 'Smith',
      email: 'john.smith@techcorp.com',
      company: 'TechCorp Inc.',
      title: 'CEO',
      industry: 'Technology',
      location: 'San Francisco, CA',
      companySize: '150 employees',
      recentNews: 'Series B funding announcement',
      ...sampleData
    };

    const request: TemplatePersonalizationRequest = {
      templateId,
      recipientData: defaultSampleData,
      campaignObjective: 'Generate qualified leads for demo',
      tone: 'professional',
      urgency: 'medium',
      includeIndustryInsights: true,
      includeSocialProof: true,
      includePersonalizedValue: true
    };

    return this.personalizeTemplate(request);
  }
}

export default TemplateService; 